"use client";

import {
  Container,
  Title,
  Text,
  Stack,
  Box,
  Card,
  Group,
  Badge,
  SimpleGrid,
  Paper,
  Button,
  Flex,
  Divider,
  Image,
  Grid,
  ThemeIcon,
  Center,
  BackgroundImage,
  Overlay,
} from "@mantine/core";
import {
  IconPalette,
  IconArrowRight,
  IconBrain,
  IconEye,
  IconSparkles,
  IconDroplet,
  IconFlame,
  IconSnowflake,
  IconLeaf,
  IconSun,
  IconMoon,
  IconStar,
} from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { colorSeasons } from "@/constants";
import UnifiedUpsell from "@/components/UnifiedUpsell";
import { useTranslations } from "next-intl";

interface SeasonalComparisonToolContentProps {
  locale: string;
}

export default function SeasonalComparisonToolContent({ locale }: SeasonalComparisonToolContentProps) {
  const t = useTranslations("seasonalComparisonTool");
  const featuredComparisons = [
    {
      title: "Soft Summer vs Cool Summer",
      description: "Discover the subtle differences between these cool summer palettes",
      href: "/soft-summer-vs-cool-summer",
      icon: IconSnowflake,
      color: "blue",
      season1: "Soft Summer",
      season2: "Cool Summer",
    },
    {
      title: "Bright Spring vs True Spring",
      description: "Compare brightness and clarity in these warm spring palettes",
      href: "/bright-spring-vs-true-spring",
      icon: IconSun,
      color: "yellow",
      season1: "Bright Spring",
      season2: "True Spring",
    },
    {
      title: "Deep Autumn vs Warm Autumn",
      description: "Explore depth and warmth in these rich autumn palettes",
      href: "/deep-autumn-vs-warm-autumn",
      icon: IconLeaf,
      color: "orange",
      season1: "Deep Autumn",
      season2: "Warm Autumn",
    },
    {
      title: "Deep Winter vs Bright Winter",
      description: "Compare intensity and coolness in these winter palettes",
      href: "/deep-winter-vs-bright-winter",
      icon: IconMoon,
      color: "grape",
      season1: "Deep Winter",
      season2: "Bright Winter",
    },
  ];

  const quickComparisons = [
    {
      title: "Soft Autumn vs Warm Autumn",
      href: "/soft-autumn-vs-warm-autumn",
      season1: "Soft Autumn",
      season2: "Warm Autumn",
      color: "orange",
    },
    {
      title: "Soft Autumn vs Deep Autumn",
      href: "/soft-autumn-vs-deep-autumn",
      season1: "Soft Autumn",
      season2: "Deep Autumn",
      color: "orange",
    },
    {
      title: "True Spring vs Bright Spring Palette",
      href: "/true-spring-vs-bright-spring-palette",
      season1: "True Spring",
      season2: "Bright Spring",
      color: "yellow",
    },
    {
      title: "Bright Spring vs Bright Winter",
      href: "/bright-spring-vs-bright-winter",
      season1: "Bright Spring",
      season2: "Bright Winter",
      color: "pink",
    },
    {
      title: "True Summer vs Soft Summer",
      href: "/true-summer-vs-soft-summer",
      season1: "True Summer",
      season2: "Soft Summer",
      color: "blue",
    },
    {
      title: "True Summer vs Cool Summer",
      href: "/true-summer-vs-cool-summer",
      season1: "True Summer",
      season2: "Cool Summer",
      color: "blue",
    },
    {
      title: "Cool Winter vs Deep Winter",
      href: "/cool-winter-vs-deep-winter",
      season1: "Cool Winter",
      season2: "Deep Winter",
      color: "grape",
    },
  ];

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Hero Section */}
        <Box ta="center" mb="xl">
          <Title order={1} size="h1" mb="md" style={{ fontSize: "3rem", fontWeight: 700 }}>
            {t("title")}
          </Title>
          <Text size="xl" c="dimmed" maw={700} mx="auto" mb="xl">
            {t("subtitle")}
          </Text>

          {/* Hero Image */}
          <Box
            style={{
              position: "relative",
              borderRadius: "1rem",
              overflow: "hidden",
              margin: "0 auto",
              maxWidth: "800px",
            }}
          >
            <Image
              src="/images/generated/cool-vs-warm-undertone-visual.jpg"
              alt="Cool vs Warm Undertone Visual Comparison"
              width={800}
              height={400}
              style={{
                borderRadius: "1rem",
                objectFit: "cover",
              }}
            />
          </Box>
        </Box>

        {/* Featured Comparisons */}
        <Box>
          <Title order={2} size="h2" ta="center" mb="lg">
            {t("featuredComparisons")}
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            {t("featuredDesc")}
          </Text>

          <Grid gutter="xl">
            {featuredComparisons.map((comparison, index) => (
              <Grid.Col key={index} span={{ base: 12, md: 6 }}>
                <Card
                  withBorder
                  p="xl"
                  radius="lg"
                  style={{
                    height: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    cursor: "pointer",
                  }}
                  component={Link}
                  href={comparison.href}
                >
                  <Stack gap="md" h="100%">
                    <Group>
                      <ThemeIcon size={48} radius="xl" color={comparison.color} variant="light">
                        <comparison.icon size={24} />
                      </ThemeIcon>
                      <Box>
                        <Title order={3} size="h3" mb="xs">
                          {comparison.title}
                        </Title>
                        <Text size="sm" c="dimmed">
                          {comparison.description}
                        </Text>
                      </Box>
                    </Group>

                    <Group justify="space-between" mt="auto">
                      <Group gap="xs">
                        <Badge color={comparison.color} variant="light" size="sm">
                          {comparison.season1}
                        </Badge>
                        <Text size="sm" c="dimmed">
                          vs
                        </Text>
                        <Badge color={comparison.color} variant="light" size="sm">
                          {comparison.season2}
                        </Badge>
                      </Group>
                      <Button
                        size="sm"
                        color={comparison.color}
                        variant="light"
                        rightSection={<IconArrowRight size={16} />}
                      >
                        {t("compare")}
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
        </Box>

        {/* Quick Comparisons */}
        <Box>
          <Title order={2} size="h2" ta="center" mb="lg">
            {t("moreComparisons")}
          </Title>
          <Text size="lg" c="dimmed" ta="center" mb="xl">
            {t("moreComparisonsDesc")}
          </Text>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {quickComparisons.map((comparison, index) => (
              <Card
                key={index}
                withBorder
                p="lg"
                radius="md"
                style={{
                  transition: "transform 0.2s, box-shadow 0.2s",
                  cursor: "pointer",
                }}
                component={Link}
                href={comparison.href}
              >
                <Stack gap="sm">
                  <Title order={4} size="h4" ta="center">
                    {comparison.title}
                  </Title>
                  <Group justify="center" gap="xs">
                    <Badge color={comparison.color} variant="light" size="sm">
                      {comparison.season1}
                    </Badge>
                    <Text size="sm" c="dimmed">
                      {t("vs")}
                    </Text>
                    <Badge color={comparison.color} variant="light" size="sm">
                      {comparison.season2}
                    </Badge>
                  </Group>
                </Stack>
              </Card>
            ))}
          </SimpleGrid>
        </Box>

        {/* How It Works */}
        <Card withBorder p="xl" radius="lg" style={{ background: "var(--mantine-color-gray-0)" }}>
          <Title order={2} size="h2" ta="center" mb="lg">
            {t("howItWorks")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl">
            <Box ta="center">
              <ThemeIcon size={64} radius="xl" color="blue" variant="light" mb="md" mx="auto">
                <IconEye size={32} />
              </ThemeIcon>
              <Title order={3} size="h3" mb="sm">
                {t("step1Title")}
              </Title>
              <Text c="dimmed">{t("step1Desc")}</Text>
            </Box>
            <Box ta="center">
              <ThemeIcon size={64} radius="xl" color="blue" variant="light" mb="md" mx="auto">
                <IconPalette size={32} />
              </ThemeIcon>
              <Title order={3} size="h3" mb="sm">
                {t("step2Title")}
              </Title>
              <Text c="dimmed">{t("step2Desc")}</Text>
            </Box>
            <Box ta="center">
              <ThemeIcon size={64} radius="xl" color="blue" variant="light" mb="md" mx="auto">
                <IconSparkles size={32} />
              </ThemeIcon>
              <Title order={3} size="h3" mb="sm">
                {t("step3Title")}
              </Title>
              <Text c="dimmed">{t("step3Desc")}</Text>
            </Box>
          </SimpleGrid>
        </Card>

        {/* CTA Section */}
        <Card
          p="xl"
          radius="lg"
          withBorder
          style={{
            background: "linear-gradient(135deg, var(--mantine-color-blue-6) 0%, var(--mantine-color-blue-8) 100%)",
            color: "white",
          }}
        >
          <Stack gap="lg" ta="center">
            <Title order={2} size="h2" c="white">
              {t("stillNotSure")}
            </Title>
            <Text size="lg" c="white" maw={500} mx="auto" opacity={0.9}>
              {t("takeQuizCta")}
            </Text>
            <Flex gap="md" justify="center" wrap="wrap">
              <Button
                size="lg"
                component={Link}
                href="/color-analysis-quiz"
                color="white"
                variant="filled"
                rightSection={<IconBrain size={20} />}
                style={{ color: "var(--mantine-color-blue-6)" }}
              >
                {t("takeColorQuiz")}
              </Button>
              <Button
                size="lg"
                variant="outline"
                component={Link}
                href="/color-palette"
                rightSection={<IconPalette size={20} />}
                style={{
                  borderColor: "white",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "white",
                    color: "var(--mantine-color-blue-6)",
                  },
                }}
              >
                {t("browsePalettes")}
              </Button>
            </Flex>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
